import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Blue Green Studio";
const DEFAULT_TITLE = "Blue Green Studio — Sustainable Architecture Firm in Bangalore";
const DEFAULT_DESC = "Research-driven, climate-responsive architecture firm in Bangalore designing eco-friendly, wellness-focused residences. Homes shaped by climate, context and culture.";
const DEFAULT_KEYWORDS = [
  "architects in Bangalore",
  "architects in Bengaluru",
  "architecture firm Bangalore",
  "Jayanagar architects",
  "home architects Bengaluru",
  "sustainable architecture Bangalore",
  "eco-friendly architects",
  "residential architects Bangalore",
  "green architecture India",
  "climate-responsive design",
  "tropical modern architecture",
  "passive cooling homes",
  "biophilic design architects",
  "rammed earth construction Bangalore",
  "wellness home design",
  "Blue Green Studio",
];

/**
 * Reusable per-page SEO block. All fields optional; sensible defaults applied.
 * `noIndex` marks admin / private routes with robots noindex,nofollow.
 */
export const Seo = ({
  title,
  description = DEFAULT_DESC,
  keywords = [],
  image = "/og-cover.jpg",
  path = "",
  type = "website",
  noIndex = false,
  jsonLd = null,
}) => {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : DEFAULT_TITLE;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}${path || (typeof window !== "undefined" ? window.location.pathname : "")}`;
  const imageUrl = image.startsWith("http") ? image : `${origin}${image}`;
  const kw = Array.from(new Set([...(keywords || []), ...DEFAULT_KEYWORDS])).join(", ");

  // index.html ships static default head tags (marked data-static-seo) so no-JS
  // crawlers still see sane defaults. Once React is running, Helmet owns the
  // head — remove the static copies to avoid duplicate title/meta tags.
  useEffect(() => {
    document.querySelectorAll("[data-static-seo]").forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={kw} />
      <link rel="canonical" href={url} />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
